import { seoKnowledgeBase, type SEOArticle, type SEOCategory } from './seo-content';
import { GLOSSARY_ARTICLES, type ArticleSource } from './glossary-articles';

// seo-content.ts is generated by scripts/generate-lexicon.mjs; hand-written
// explainers replace generated entries with the same slug here instead.
const OVERRIDES = new Map(GLOSSARY_ARTICLES.map((article) => [article.slug, article]));

const withOverride = (article: SEOArticle): SEOArticle => OVERRIDES.get(article.slug) ?? article;

export const knowledgeBase: SEOCategory[] = seoKnowledgeBase.map((category) => ({
  ...category,
  articles: category.articles.map(withOverride),
}));

export const getAllArticles = (): SEOArticle[] =>
  knowledgeBase.flatMap((category) => category.articles);

// The pages an explainer's facts come from. Generated entries have none.
export const getArticleSources = (slug: string): ArticleSource[] => OVERRIDES.get(slug)?.sources ?? [];

const GLOSSARY_CATEGORY = 'corporate-ai-travel-glossary';

// Glossary entries still on the generated template (the same paragraphs with
// the term swapped). They stay live and linked from the hub, but are kept out
// of search results and the sitemap until each gets a real explainer in
// glossary-articles.ts, which takes it off this list automatically.
const TEMPLATED_SLUGS = new Set(
  seoKnowledgeBase
    .filter((category) => category.slug === GLOSSARY_CATEGORY)
    .flatMap((category) => category.articles)
    .map((article) => article.slug)
    .filter((slug) => !OVERRIDES.has(slug)),
);

// Kept live but out of search: a comparison that characterises a named
// competitor without sources. Re-index it only once every claim about the
// other product is dated and linked to that vendor's own pages.
const UNSOURCED_SLUGS = new Set(['voyara-ai-vs-makemytrip-corporate']);

export const isIndexable = (slug: string): boolean =>
  !TEMPLATED_SLUGS.has(slug) && !UNSOURCED_SLUGS.has(slug);

export const getIndexableArticleSlugs = (): string[] =>
  getAllArticles().map((article) => article.slug).filter(isIndexable);

// Articles that explain each other. Without these, every article linked only
// back to /knowledge and never to a neighbour. Terms still on the template
// are skipped until they are rewritten.
const RELATED_ARTICLES: Record<string, string[]> = {
  'flight-delay-prediction-ai': ['smart-itinerary-optimization', 'death-of-static-itinerary', 'autonomous-multi-agent-travel'],
  'smart-itinerary-optimization': ['flight-delay-prediction-ai', 'death-of-static-itinerary', 'decarbonizing-travel-route-optimization'],
  'autonomous-multi-agent-travel': ['business-travel-orchestration-2026', 'smart-itinerary-optimization', 'enterprise-travel-saas-vs-orchestration'],
  'death-of-static-itinerary': ['smart-itinerary-optimization', 'flight-delay-prediction-ai', 'business-travel-orchestration-2026'],
  'generative-engine-optimization-travel': ['voyara-ai-eclipse-group-ecosystem', 'vyora-vayora-voyara-ai', 'autonomous-multi-agent-travel'],
  'business-travel-orchestration-2026': ['enterprise-travel-saas-vs-orchestration', 'online-booking-tool-obt', 'autonomous-multi-agent-travel'],
  'enterprise-travel-saas-vs-orchestration': ['business-travel-orchestration-2026', 'online-booking-tool-obt', 'global-distribution-system-gds'],
  'decarbonizing-travel-route-optimization': ['smart-itinerary-optimization', 'business-travel-orchestration-2026', 'enterprise-travel-saas-vs-orchestration'],
  'voyara-ai-eclipse-group-ecosystem': ['eclipse-group-vs-eclipse-foundation', 'vyora-vayora-voyara-ai', 'business-travel-orchestration-2026'],
  'eclipse-group-vs-eclipse-foundation': ['voyara-ai-eclipse-group-ecosystem', 'vyora-vayora-voyara-ai', 'generative-engine-optimization-travel'],
  'vyora-vayora-voyara-ai': ['voyara-ai-eclipse-group-ecosystem', 'eclipse-group-vs-eclipse-foundation', 'flight-delay-prediction-ai'],
  'computer-reservation-system-crs': ['global-distribution-system-gds', 'passenger-name-record-pnr', 'new-distribution-capability-ndc'],
  'global-distribution-system-gds': ['computer-reservation-system-crs', 'new-distribution-capability-ndc', 'passenger-name-record-pnr', 'travel-management-company-tmc'],
  'passenger-name-record-pnr': ['global-distribution-system-gds', 'computer-reservation-system-crs', 'international-air-transport-association-iata'],
  'online-booking-tool-obt': ['travel-management-company-tmc', 'virtual-credit-cards', 'global-distribution-system-gds'],
  'virtual-credit-cards': ['online-booking-tool-obt', 'travel-management-company-tmc'],
};

export const getRelatedArticles = (slug: string): SEOArticle[] => {
  const articles = getAllArticles();
  return (RELATED_ARTICLES[slug] ?? [])
    .filter((related) => isIndexable(related))
    .map((related) => articles.find((article) => article.slug === related))
    .filter((article): article is SEOArticle => article !== undefined);
};
